import { useState, useEffect, useCallback } from 'react'
    import { supabase } from '../config/supabase'
    import {
      getRelationshipBasedOnSex,
      getOppositeRelationship,
      getBaseRelationshipType,
    } from '../utils/relationshipMappings'
    import {
      generateSmartFamilyRelationships,
      relationshipExists,
    } from '../utils/familyLogic'

    // Helper function to capitalize words
    const capitalizeWords = (str) => {
      if (!str) return ''
      return str
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    }

    // People hook
    export const usePeople = () => {
      const [people, setPeople] = useState([])
      const [loading, setLoading] = useState(false)
      const [error, setError] = useState(null)

      const fetchPeople = useCallback(async () => {
        try {
          setLoading(true)
          setError(null)
          const {
            data: { user },
          } = await supabase.auth.getUser()
          if (!user) {
            console.warn('Attempted to fetch people without an authenticated user.')
            setPeople([])
            return []
          }
          const userId = user.id
          console.log('Fetching people for user:', userId)

          const { data, error } = await supabase
            .from('people_tracker_2024')
            .select('*')
            .eq('user_id', userId)
            .order('name')

          if (error) {
            console.error('Database error:', error)
            throw error
          }
          console.log('Fetched people:', data?.length || 0)
          setPeople(data || [])
          return data || []
        } catch (err) {
          console.error('Error fetching people:', err)
          setError(err.message)
          return []
        } finally {
          setLoading(false)
        }
      }, [])

      const searchPeople = useCallback(async (query) => {
        try {
          setError(null)
          const {
            data: { user },
          } = await supabase.auth.getUser()
          if (!user) {
            console.warn('Attempted to search people without an authenticated user.')
            return []
          }
          const userId = user.id

          // Split the query into individual words and create search patterns
          const words = query
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0)
          if (words.length === 0) {
            return []
          }

          // Create search conditions for each word with wildcards
          const searchConditions = words
            .map((word) => {
              const wildcardWord = `%${word}%`
              return `name.ilike.${wildcardWord},nicknames.ilike.${wildcardWord},notes.ilike.${wildcardWord}`
            })
            .join(',')

          console.log('Search query:', query)
          console.log('Search words:', words)
          console.log('Search conditions:', searchConditions)

          const { data, error } = await supabase
            .from('people_tracker_2024')
            .select('*')
            .eq('user_id', userId)
            .or(searchConditions)
            .order('name')

          if (error) throw error

          // Additional client-side filtering for multi-word searches
          // This ensures ALL words must match somewhere in the person's data
          const filteredData = (data || []).filter((person) => {
            const searchText =
              `${person.name} ${person.nicknames || ''} ${person.notes || ''}`.toLowerCase()
            return words.every((word) => searchText.includes(word.toLowerCase()))
          })

          return filteredData
        } catch (err) {
          console.error('Error searching people:', err)
          setError(err.message)
          return []
        }
      }, [])

      const uploadPhoto = async (file, personId) => {
        try {
          const fileExt = file.name.split('.').pop()
          const fileName = `${personId}_${Date.now()}.${fileExt}`
          const filePath = `photos/${fileName}`

          const { error: uploadError } = await supabase.storage
            .from('person-photos')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false,
            })

          if (uploadError) throw uploadError

          const {
            data: { publicUrl },
          } = await supabase.storage.from('person-photos').getPublicUrl(filePath)

          return publicUrl
        } catch (error) {
          console.error('Error uploading photo:', error)
          throw error
        }
      }

      const addPerson = useCallback(async (personData) => {
        try {
          setLoading(true)
          setError(null)
          const {
            data: { user },
          } = await supabase.auth.getUser()
          if (!user) throw new Error('User not authenticated. Cannot add person.')
          const userId = user.id

          // Prepare the data without the photo file
          const { photoFile, ...dataToInsert } = personData
          dataToInsert.user_id = userId

          // Capitalize name and nicknames
          if (dataToInsert.name) {
            dataToInsert.name = capitalizeWords(dataToInsert.name.trim())
          }
          if (dataToInsert.nicknames) {
            dataToInsert.nicknames = dataToInsert.nicknames
              .split(',')
              .map((n) => capitalizeWords(n.trim()))
              .join(',')
          }

          // CRITICAL FIX: Ensure proximity value has the correct case (Title Case)
          // This prevents the database constraint violation
          const validProximities = ['Close', 'Medium', 'Far']
          if (!validProximities.includes(dataToInsert.proximity)) {
            dataToInsert.proximity = 'Medium' // Default value with proper case
          }

          console.log('Adding person with data:', dataToInsert)

          // First insert the person to get the ID
          const { data, error } = await supabase
            .from('people_tracker_2024')
            .insert([dataToInsert])
            .select()

          if (error) {
            console.error('Insert error:', error)
            throw error
          }

          if (!data || data.length === 0) {
            throw new Error('No data returned from insert')
          }

          let finalPerson = data[0]

          // If there's a photo file, upload it and update the person
          if (photoFile) {
            try {
              const photoUrl = await uploadPhoto(photoFile, finalPerson.id)
              const { data: updatedData, error: updateError } = await supabase
                .from('people_tracker_2024')
                .update({ photo_url: photoUrl })
                .eq('id', finalPerson.id)
                .select()
              if (updateError) throw updateError
              finalPerson = updatedData[0]
            } catch (photoError) {
              console.error('Error uploading photo:', photoError)
              // Continue without photo if upload fails
            }
          }

          // Update the local state with the new person
          setPeople((prev) => [...prev, finalPerson])

          return finalPerson
        } catch (err) {
          console.error('Error adding person:', err)
          setError(err.message)
          throw err
        } finally {
          setLoading(false)
        }
      }, [])

      const updatePerson = useCallback(async (id, personData) => {
        try {
          setLoading(true)
          setError(null)
          const {
            data: { user },
          } = await supabase.auth.getUser()
          if (!user) throw new Error('User not authenticated. Cannot update person.')
          const userId = user.id

          // Prepare the data without the photo file
          const { photoFile, ...dataToUpdate } = personData
          dataToUpdate.updated_at = new Date().toISOString()

          // Capitalize name and nicknames
          if (dataToUpdate.name) {
            dataToUpdate.name = capitalizeWords(dataToUpdate.name.trim())
          }
          if (dataToUpdate.nicknames) {
            dataToUpdate.nicknames = dataToUpdate.nicknames
              .split(',')
              .map((n) => capitalizeWords(n.trim()))
              .join(',')
          }

          // CRITICAL FIX: Ensure proximity value has the correct case (Title Case)
          // This prevents the database constraint violation
          const validProximities = ['Close', 'Medium', 'Far']
          if (!validProximities.includes(dataToUpdate.proximity)) {
            dataToUpdate.proximity = 'Medium' // Default value with proper case
          }

          console.log('Updating person with data:', dataToUpdate)

          // If there's a photo file, upload it first
          if (photoFile) {
            try {
              const photoUrl = await uploadPhoto(photoFile, id)
              dataToUpdate.photo_url = photoUrl
            } catch (photoError) {
              console.error('Error uploading photo:', photoError)
              // Continue without photo update if upload fails
            }
          }

          // FIXED: Now actually updating the person data instead of just selecting it
          const { data, error } = await supabase
            .from('people_tracker_2024')
            .update(dataToUpdate)
            .eq('id', id)
            .eq('user_id', userId)
            .select()

          if (error) {
            console.error('Update error:', error)
            throw error
          }

          if (!data || data.length === 0) {
            throw new Error('Person not found or not updated')
          }

          setPeople((prev) => prev.map((p) => (p.id === id ? data[0] : p)))
          return data[0]
        } catch (err) {
          console.error('Error updating person:', err)
          setError(err.message)
          throw err
        } finally {
          setLoading(false)
        }
      }, [])

      const deletePerson = useCallback(async (id) => {
        try {
          setLoading(true)
          setError(null)
          const {
            data: { user },
          } = await supabase.auth.getUser()
          if (!user) throw new Error('User not authenticated. Cannot delete person.')
          const userId = user.id
          const { data, error } = await supabase
            .from('people_tracker_2024')
            .delete()
            .eq('id', id)
            .eq('user_id', userId)
            .select()

          // Confirm deletion
          if (error) throw error

          if (!data || data.length === 0) {
            throw new Error(
              'Deletion failed. The person might not exist or you may not have permission.'
            )
          }

          setPeople((prev) => prev.filter((p) => p.id !== id))
        } catch (err) {
          console.error('Error deleting person:', err)
          setError(err.message)
          throw err
        } finally {
          setLoading(false)
        }
      }, [])

      return {
        people,
        loading,
        error,
        fetchPeople,
        searchPeople,
        addPerson,
        updatePerson,
        deletePerson,
      }
    }

    // Relationships hook with FIXED smart family logic
    export const useRelationships = () => {
      const [relationships, setRelationships] = useState([])
      const [loading, setLoading] = useState(false)
      const [error, setError] = useState(null)

      const fetchRelationships = useCallback(async (personId = null) => {
        try {
          setLoading(true)
          setError(null)
          const {
            data: { user },
          } = await supabase.auth.getUser()
          if (!user) {
            console.warn(
              'Attempted to fetch relationships without an authenticated user.'
            )
            setRelationships([])
            return []
          }
          const userId = user.id

          let query = supabase
            .from('relationships_tracker_2024')
            .select(
              `
        *,
        person_a:people_tracker_2024!relationships_tracker_2024_person_a_id_fkey(id, name, sex),
        person_b:people_tracker_2024!relationships_tracker_2024_person_b_id_fkey(id, name, sex)
      `
            )
            .eq('user_id', userId)

          if (personId) {
            query = query.or(
              `person_a_id.eq.${personId},person_b_id.eq.${personId}`
            )
          }

          const { data, error } = await query

          if (error) {
            console.error('Database error:', error)
            throw error
          }
          console.log('Fetched relationships:', data)
          setRelationships(data || [])
          return data || []
        } catch (error) {
          console.error('Error fetching relationships:', error)
          setError(error.message)
          return []
        } finally {
          setLoading(false)
        }
      }, [])

      // COMPLETELY FIXED: Enhanced addRelationship with proper smart family logic and duplicate prevention
      const addRelationship = useCallback(async (
        currentPersonId,
        otherPersonId,
        relationshipTypeOfOtherPerson,
        allPeople = [],
        allRelationships = []
      ) => {
        try {
          setLoading(true)
          setError(null)
          const {
            data: { user },
          } = await supabase.auth.getUser()
          if (!user)
            throw new Error('User not authenticated. Cannot add relationship.')
          const userId = user.id

          console.log('\n🎯===ADDING RELATIONSHIP===')
          console.log('Current person ID:', currentPersonId)
          console.log('Other person ID:', otherPersonId)
          console.log('Relationship type:', relationshipTypeOfOtherPerson)
          console.log('All people count:', allPeople.length)
          console.log('All relationships count:', allRelationships.length)

          // CRITICAL: Check if relationship already exists between these two people
          if (
            relationshipExists(currentPersonId, otherPersonId, allRelationships)
          ) {
            console.log(
              '⚠️ Relationship already exists between these people - skipping creation'
            )
            // Find the existing relationship
            const existingRelationship = allRelationships.find(
              (rel) =>
                (rel.person_a_id === currentPersonId &&
                  rel.person_b_id === otherPersonId) ||
                (rel.person_a_id === otherPersonId &&
                  rel.person_b_id === currentPersonId)
            )
            if (existingRelationship) {
              console.log('✓ Using existing relationship:', existingRelationship)
              // Return empty smart relationships since this is a duplicate
              return {
                mainRelationship: existingRelationship,
                smartRelationships: [],
                smartRelationshipCount: 0,
                explanations: ['Relationship already existed'],
              }
            }
          }

          // Get both people's details
          const { data: peopleData, error: peopleError } = await supabase
            .from('people_tracker_2024')
            .select('id, name, sex')
            .in('id', [currentPersonId, otherPersonId])

          if (peopleError) {
            console.error('People fetch error:', peopleError)
            throw peopleError
          }

          const currentPerson = peopleData.find((p) => p.id === currentPersonId)
          const otherPerson = peopleData.find((p) => p.id === otherPersonId)

          if (!currentPerson || !otherPerson) {
            throw new Error('Could not find both people')
          }

          console.log('👤 Current person:', currentPerson.name, currentPerson.sex)
          console.log('👤 Other person:', otherPerson.name, otherPerson.sex)

          // CRITICAL FIX: Apply proper relationship transformations with correct perspective
          // When adding a relationship, relationshipTypeOfOtherPerson represents what the other person is to the current person
          const baseRelationshipType = getBaseRelationshipType(
            relationshipTypeOfOtherPerson
          )
          const oppositeBaseType = getOppositeRelationship(
            baseRelationshipType,
            null
          )

          // What the other person is to the current person (with sex-specific transformation)
          const otherToCurrentRelationship = getRelationshipBasedOnSex(
            baseRelationshipType,
            otherPerson.sex
          )
          // What the current person is to the other person (with sex-specific transformation)
          const currentToOtherRelationship = getRelationshipBasedOnSex(
            oppositeBaseType,
            currentPerson.sex
          )

          console.log(
            '🔗 Other to current (specific):',
            otherToCurrentRelationship
          )
          console.log(
            '🔗 Current to other (specific):',
            currentToOtherRelationship
          )

          // Store the relationship
          const relationshipData = {
            user_id: userId,
            person_a_id: currentPersonId,
            person_b_id: otherPersonId,
            relationship_type: currentToOtherRelationship, // What person A is to person B
            relationship_type_b: otherToCurrentRelationship, // What person B is to person A
          }

          console.log('💾 Inserting relationship:', relationshipData)

          // Insert the new relationship
          const { data, error } = await supabase
            .from('relationships_tracker_2024')
            .insert([relationshipData])
            .select()

          if (error) {
            console.error('❌ Insert error:', error)
            throw error
          }

          if (!data || data.length === 0) {
            throw new Error('No data returned from insert')
          }
          const newRelationship = data[0]
          console.log('✅ Main relationship created:', newRelationship)

          // Update local state with the new relationship
          setRelationships((prev) => [...prev, newRelationship])

          // GENERATE SMART FAMILY RELATIONSHIPS
          console.log('\n🧠===GENERATING SMART RELATIONSHIPS===')
          // Include both the allRelationships passed in and the newly created relationship
          // This is critical for preventing duplicates
          const currentRelationships = [...allRelationships, newRelationship]
          const smartRelationships = generateSmartFamilyRelationships(
            currentPersonId,
            otherPersonId,
            baseRelationshipType, // Use base type for smart logic
            currentRelationships,
            allPeople
          )
          console.log(
            `📊 Generated ${smartRelationships.length} smart relationships`
          )

          // Add smart relationships to database
          const addedSmartRelationships = []
          let smartRelationshipCount = 0
          for (const smartRel of smartRelationships) {
            console.log(
              `\n🔄 Processing smart relationship: ${smartRel.explanation}`
            )
            // Check if relationship already exists
            // Include the already added smart relationships to prevent duplicates within this batch
            const existingRelationships = [
              ...currentRelationships,
              ...addedSmartRelationships,
            ]
            if (
              !relationshipExists(
                smartRel.person_a_id,
                smartRel.person_b_id,
                existingRelationships
              )
            ) {
              try {
                // Get person details for the smart relationship
                const personA = allPeople.find(
                  (p) => p.id === smartRel.person_a_id
                )
                const personB = allPeople.find(
                  (p) => p.id === smartRel.person_b_id
                )

                if (personA && personB) {
                  console.log(
                    ` 👥 Creating smart relationship between ${personA.name} and ${personB.name}`
                  )
                  // CORRECTED LOGIC BLOCK
                  // smartRel.relationshipType is what B is to A.
                  const what_B_is_to_A_base = getBaseRelationshipType(
                    smartRel.relationshipType
                  )
                  // What A is to B is the opposite.
                  const what_A_is_to_B_base = getOppositeRelationship(
                    what_B_is_to_A_base,
                    null
                  )

                  // Get the specific terms based on sex
                  const aToB_specific = getRelationshipBasedOnSex(
                    what_A_is_to_B_base, // what A is to B
                    personA.sex
                  )
                  const bToA_specific = getRelationshipBasedOnSex(
                    what_B_is_to_A_base, // what B is to A
                    personB.sex
                  )

                  const smartRelData = {
                    user_id: userId,
                    person_a_id: smartRel.person_a_id,
                    person_b_id: smartRel.person_b_id,
                    relationship_type: aToB_specific, // What person A is to person B
                    relationship_type_b: bToA_specific, // What person B is to person A
                  }
                  console.log(' 💾 Smart relationship data:', smartRelData)

                  const { data: smartData, error: smartError } = await supabase
                    .from('relationships_tracker_2024')
                    .insert([smartRelData])
                    .select()

                  if (smartError) {
                    console.error(' ❌ Smart relationship error:', smartError)
                  } else if (smartData && smartData.length > 0) {
                    addedSmartRelationships.push(smartData[0])
                    smartRelationshipCount++
                    console.log(
                      ` ✅ Smart relationship created: ${smartRel.explanation}`
                    )
                  }
                } else {
                  console.log(
                    ` ⚠️ Could not find people for smart relationship: ${smartRel.person_a_id}, ${smartRel.person_b_id}`
                  )
                }
              } catch (smartErr) {
                console.error(' ❌ Error adding smart relationship:', smartErr)
              }
            } else {
              console.log(
                ` ⚠️ Smart relationship already exists: ${smartRel.explanation}`
              )
            }
          }

          // Update local state with smart relationships
          if (addedSmartRelationships.length > 0) {
            setRelationships((prev) => [...prev, ...addedSmartRelationships])
            console.log(
              `\n🎉 Added ${addedSmartRelationships.length} smart relationships to database`
            )
          }
          console.log('\n✅===RELATIONSHIP CREATION COMPLETE===')

          return {
            mainRelationship: newRelationship,
            smartRelationships: addedSmartRelationships,
            smartRelationshipCount: smartRelationshipCount,
            explanations: smartRelationships.map((sr) => sr.explanation),
          }
        } catch (error) {
          console.error('❌ Error adding relationship:', error)
          setError(error.message)
          throw error
        } finally {
          setLoading(false)
        }
      }, [])

      // CRITICAL FIX: updateRelationship now accepts currentPersonId to handle perspective correctly
      const updateRelationship = useCallback(async (
        relationshipId,
        updateData,
        currentPersonId
      ) => {
        try {
          setLoading(true)
          setError(null)
          const {
            data: { user },
          } = await supabase.auth.getUser()
          if (!user)
            throw new Error('User not authenticated. Cannot update relationship.')
          const userId = user.id

          console.log('Updating relationship:', {
            relationshipId,
            updateData,
            currentPersonId,
          })

          // Get the existing relationship with person details
          const { data: existingRel, error: fetchError } = await supabase
            .from('relationships_tracker_2024')
            .select(
              `
          *,
          person_a:people_tracker_2024!relationships_tracker_2024_person_a_id_fkey(id, name, sex),
          person_b:people_tracker_2024!relationships_tracker_2024_person_b_id_fkey(id, name, sex)
        `
            )
            .eq('id', relationshipId)
            .eq('user_id', userId)
            .single()

          if (fetchError) {
            console.error('Fetch error:', fetchError)
            throw fetchError
          }
          if (!existingRel) {
            throw new Error('Relationship not found')
          }

          // Determine if the person from whose perspective we are editing is person_a or person_b
          const isCurrentPersonA = existingRel.person_a_id === currentPersonId

          // `updateData.relationshipType` is the base type of what the "other person" is to the "current person"
          const otherPersonBaseType = getBaseRelationshipType(
            updateData.relationshipType
          ) // e.g., 'sibling_child'
          const currentPersonBaseType = getOppositeRelationship(
            otherPersonBaseType,
            null
          ) // e.g., 'parental_sibling'

          let personAToPersonB_specific, personBToPersonA_specific

          if (isCurrentPersonA) {
            // Current is A, Other is B. The selected type is what B is to A.
            // So, otherPersonBaseType is what B is to A.
            personBToPersonA_specific = getRelationshipBasedOnSex(
              otherPersonBaseType, // Correct
              existingRel.person_b.sex
            ) // e.g., nephew
            // And currentPersonBaseType is what A is to B.
            personAToPersonB_specific = getRelationshipBasedOnSex(
              currentPersonBaseType, // Correct
              existingRel.person_a.sex
            ) // e.g., aunt
          } else {
            // Current is B, Other is A. The selected type is what A is to B.
            // So, otherPersonBaseType is what A is to B.
            personAToPersonB_specific = getRelationshipBasedOnSex(
              otherPersonBaseType, // Correct
              existingRel.person_a.sex
            ) // e.g., nephew
            // And currentPersonBaseType is what B is to A.
            personBToPersonA_specific = getRelationshipBasedOnSex(
              currentPersonBaseType, // Correct
              existingRel.person_b.sex
            ) // e.g., aunt
          }

          const updateObj = {
            relationship_type: personAToPersonB_specific, // What person A is to person B
            relationship_type_b: personBToPersonA_specific, // What person B is to person A
            updated_at: new Date().toISOString(),
          }
          console.log('Update object:', updateObj)

          const { data, error } = await supabase
            .from('relationships_tracker_2024')
            .update(updateObj)
            .eq('id', relationshipId)
            .eq('user_id', userId)
            .select()

          if (error) {
            console.error('Update error:', error)
            throw error
          }

          if (!data || data.length === 0) {
            throw new Error('No data returned from update')
          }

          console.log('Updated relationship:', data[0])
          const updatedRelationship = data[0]

          setRelationships((prev) =>
            prev.map((r) => (r.id === relationshipId ? updatedRelationship : r))
          )
          return updatedRelationship
        } catch (error) {
          console.error('Error updating relationship:', error)
          setError(error.message)
          throw error
        } finally {
          setLoading(false)
        }
      }, [])

      const deleteRelationship = useCallback(async (relationshipId) => {
        try {
          setLoading(true)
          setError(null)
          const {
            data: { user },
          } = await supabase.auth.getUser()
          if (!user)
            throw new Error('User not authenticated. Cannot delete relationship.')
          const userId = user.id
          // Scope the delete to the current user so RLS checks succeed reliably.
          const { error, data } = await supabase
            .from('relationships_tracker_2024')
            .delete()
            .eq('id', relationshipId)
            .eq('user_id', userId)
            .select('id')
          if (error) {
            console.error('Delete error:', error)
            throw error // This will be caught by the component.
          }
          if (!data || data.length === 0) {
            throw new Error('Relationship not found or already deleted')
          }
          // Update the hook's internal state. The component will update its own state.
          setRelationships((prev) => prev.filter((r) => r.id !== relationshipId))
        } catch (error) {
          console.error('Error deleting relationship:', error)
          setError(error.message)
          throw error // Re-throw to ensure the component's catch block runs.
        } finally {
          setLoading(false)
        }
      }, [])

      // Add a method to cleanup duplicate relationships
      const cleanupDuplicateRelationships = useCallback(async () => {
        try {
          setLoading(true)
          setError(null)
          const {
            data: { user },
          } = await supabase.auth.getUser()
          if (!user)
            throw new Error('User not authenticated. Cannot cleanup relationships.')
          const userId = user.id
          console.log('Starting duplicate relationship cleanup')

          // First, identify duplicate relationships
          const { data: relationshipsData, error: fetchError } = await supabase
            .from('relationships_tracker_2024')
            .select('*')
            .eq('user_id', userId)

          if (fetchError) throw fetchError

          const relationships = relationshipsData || []
          const seenPairs = new Map()
          const duplicates = []

          // Find duplicates (keeping the newest one)
          relationships.forEach((rel) => {
            // Create a unique key for each person pair, regardless of A/B order
            const personA = rel.person_a_id
            const personB = rel.person_b_id
            const pairKey = [personA, personB].sort().join('_')

            if (!seenPairs.has(pairKey)) {
              seenPairs.set(pairKey, rel)
            } else {
              // Compare created_at dates and keep the newer one
              const existingRel = seenPairs.get(pairKey)
              const existingDate = new Date(existingRel.created_at)
              const currentDate = new Date(rel.created_at)

              if (currentDate > existingDate) {
                // Current is newer, add existing to duplicates
                duplicates.push(existingRel.id)
                seenPairs.set(pairKey, rel)
              } else {
                // Existing is newer or same, add current to duplicates
                duplicates.push(rel.id)
              }
            }
          })

          console.log(`Found ${duplicates.length} duplicate relationships`)

          // Delete duplicates if found
          if (duplicates.length > 0) {
            const { error: deleteError } = await supabase
              .from('relationships_tracker_2024')
              .delete()
              .in('id', duplicates)
              .eq('user_id', userId)
            if (deleteError) throw deleteError
            console.log(
              `Successfully deleted ${duplicates.length} duplicate relationships`
            )
            // Refresh relationships after cleanup
            await fetchRelationships()
          }
          return { cleaned: duplicates.length }
        } catch (error) {
          console.error('Error cleaning up duplicate relationships:', error)
          setError(error.message)
          throw error
        } finally {
          setLoading(false)
        }
      }, [fetchRelationships])

      const syncAllFamilyRelationships = useCallback(async () => {
        try {
          setLoading(true)
          setError(null)
          const {
            data: { user },
          } = await supabase.auth.getUser()
          if (!user)
            throw new Error(
              'User not authenticated. Cannot sync family relationships.'
            )
          const userId = user.id
          console.log('--- STARTING FULL FAMILY SYNC ---')
          // 1. Fetch all people and relationships
          const { data: allPeople, error: peopleError } = await supabase
            .from('people_tracker_2024')
            .select('*')
            .eq('user_id', userId)
          if (peopleError) throw peopleError

          const { data: initialRelationships, error: relsError } = await supabase
            .from('relationships_tracker_2024')
            .select('*')
            .eq('user_id', userId)
          if (relsError) throw relsError

          console.log(
            `Found ${allPeople.length} people and ${initialRelationships.length} relationships.`
          )

          // 2. Setup for processing
          let relationshipsToProcess = [...initialRelationships]
          let allKnownRelationships = [...initialRelationships]
          let newlyAddedRelationships = []
          let iterations = 0
          const MAX_ITERATIONS = 5000 // Safety break to prevent infinite loops

          // 3. Process relationships iteratively
          while (
            relationshipsToProcess.length > 0 &&
            iterations < MAX_ITERATIONS
          ) {
            iterations++
            const rel = relationshipsToProcess.shift()
            const baseType = getBaseRelationshipType(rel.relationship_type_b)
            const familyBaseTypes = [
              'spouse',
              'parent',
              'child',
              'sibling',
              'grandparent',
              'grandchild',
              'parental_sibling',
              'sibling_child',
            ]
            if (!familyBaseTypes.includes(baseType)) {
              continue
            }
            const smartRels = generateSmartFamilyRelationships(
              rel.person_a_id,
              rel.person_b_id,
              baseType,
              allKnownRelationships, // Pass the most current list
              allPeople
            )
            for (const smartRel of smartRels) {
              if (
                !relationshipExists(
                  smartRel.person_a_id,
                  smartRel.person_b_id,
                  allKnownRelationships
                )
              ) {
                const personA = allPeople.find(
                  (p) => p.id === smartRel.person_a_id
                )
                const personB = allPeople.find(
                  (p) => p.id === smartRel.person_b_id
                )
                if (personA && personB) {
                  const smartBaseType = getBaseRelationshipType(
                    smartRel.relationshipType
                  )
                  const smartOppositeType = getOppositeRelationship(
                    smartBaseType,
                    null
                  )
                  const aToB_specific = getRelationshipBasedOnSex(
                    smartBaseType,
                    personA.sex
                  )
                  const bToA_specific = getRelationshipBasedOnSex(
                    smartOppositeType,
                    personB.sex
                  )
                  const fullNewRel = {
                    user_id: userId,
                    person_a_id: personA.id,
                    person_b_id: personB.id,
                    relationship_type: aToB_specific,
                    relationship_type_b: bToA_specific,
                  }
                  allKnownRelationships.push(fullNewRel)
                  relationshipsToProcess.push(fullNewRel)
                  newlyAddedRelationships.push(fullNewRel)
                  console.log(
                    `[Sync] Found new relationship: ${smartRel.explanation}`
                  )
                }
              }
            }
          }

          if (iterations >= MAX_ITERATIONS) {
            console.warn(
              'Family sync reached max iterations. Some relationships might be missing.'
            )
          }

          console.log(
            `Found ${newlyAddedRelationships.length} new relationships to add.`
          )

          // 4. Batch insert new relationships
          if (newlyAddedRelationships.length > 0) {
            const relationshipsToInsert = newlyAddedRelationships.map(
              (rel) => ({
                user_id: rel.user_id,
                person_a_id: rel.person_a_id,
                person_b_id: rel.person_b_id,
                relationship_type: rel.relationship_type,
                relationship_type_b: rel.relationship_type_b,
              })
            )

            const { error: insertError } = await supabase
              .from('relationships_tracker_2024')
              .insert(relationshipsToInsert)

            if (insertError) {
              console.error(
                'Error batch inserting new relationships:',
                insertError
              )
              return { addedCount: 0, error: insertError }
            }
          }

          console.log('--- FAMILY SYNC COMPLETE ---')
          return { addedCount: newlyAddedRelationships.length }
        } catch (error) {
          console.error('Error during family sync:', error)
          setError(error.message)
          throw error
        } finally {
          setLoading(false)
        }
      }, [])

      return {
        relationships,
        loading,
        error,
        fetchRelationships,
        addRelationship,
        updateRelationship,
        deleteRelationship,
        cleanupDuplicateRelationships, // New method to clean up duplicates
        syncAllFamilyRelationships,
      }
    }